import { css, Input, Stack, useMediaQuery, useTheme } from '@mui/material'
import {
  FC,
  SVGAttributes,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { unifiedColors } from 'styles/colors'
import { Theme, themeUtils } from 'styles/theme'
import { StyledHeaderLabel } from './SearchBar.style'
import { SearchBarContext } from './SearchBarContext'
import { SearchTab } from './SearchBarHeader'

interface HeaderInputProps {
  placeholder: string
  value: string
  setSearchLabel: (value: string) => void
  searchLabel: string
  Icon: FC<SVGAttributes<SVGElement>>
  openOnMount: boolean
  submit?: () => void
  searchTab: SearchTab
}

export const HeaderInput = ({
  value,
  searchLabel,
  setSearchLabel,
  placeholder,
  Icon,
  openOnMount,
  submit,
  searchTab
}: HeaderInputProps) => {
  const [showInput, setShowInput] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const theme: Theme = useTheme()
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)
  const { raceType, typeAutocomplete, locationAutocomplete } =
    useContext(SearchBarContext)
  const autoComplete =
    searchTab === 'location' ? locationAutocomplete : typeAutocomplete

  useEffect(() => {
    if (!openOnMount || (raceType?.label && isMobile)) return
    setShowInput(true)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }, [])

  useEffect(() => {
    if (searchTab === 'location' && openOnMount) {
      setShowInput(true)
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }, [searchTab])

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="6px"
      padding="15px 25px 15px 10px"
      maxWidth="100%"
      width="100%"
      onClick={() => {
        setShowInput(true)
        setTimeout(() => {
          inputRef.current?.focus()
        }, 0)
      }}
    >
      <Icon
        css={css`
          min-width: 18px;
        `}
      />
      {!showInput ? (
        <StyledHeaderLabel label={value} />
      ) : (
        <Input
          sx={{
            width: '100%',
            '&:after, &:before': {
              content: 'unset'
            }
          }}
          slotProps={{
            input: {
              sx: {
                ...theme.typography.body2,
                fontSize: { xs: 16, md: 14 },
                height: 'auto',
                maxWidth: '100%',
                padding: '0',
                color: unifiedColors.label.onPurple
              }
            }
          }}
          onKeyDown={(e) => {
            if (submit && e.key === 'Enter') {
              submit()
            }
          }}
          onFocus={() => {
            autoComplete.setIsOpen(true)
            autoComplete.setQuery('')
            autoComplete.refresh()
          }}
          inputRef={inputRef}
          placeholder={placeholder}
          onBlur={() => setShowInput(false)}
          autoFocus
          value={searchLabel}
          onChange={(e) => {
            setSearchLabel(e.target.value)
            autoComplete.setQuery(e.target.value)
            autoComplete.refresh()
          }}
        />
      )}
    </Stack>
  )
}
